// SEO-friendly slug helpers for provider URLs.
// Format: {name}-{specialty}-{city}-{state}-{publicId}
// where publicId is the provider's HL####### listing ID.
// Falls back to first 8 chars of UUID when public_id is missing
// (legacy data, until backfilled by the DB trigger).

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PUBLIC_ID_RE = /^HL\d{7}$/i;

function slugify(input: string | null | undefined): string {
  if (!input) return "";
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

export function shortIdFromUuid(id: string): string {
  return id.replace(/-/g, "").slice(0, 8).toLowerCase();
}

export function buildProviderSlug(p: {
  id: string;
  public_id?: string | null;
  name?: string | null;
  specialty?: string | null;
  city?: string | null;
  state?: string | null;
}): string {
  const parts = [p.name, p.specialty, p.city, p.state]
    .map((s) => slugify(s))
    .filter(Boolean)
    .join("-");
  // Prefer the human-readable HL####### public_id; fall back to UUID short.
  const tail = p.public_id && PUBLIC_ID_RE.test(p.public_id)
    ? p.public_id.toUpperCase()
    : shortIdFromUuid(p.id);
  return parts ? `${parts}-${tail}` : tail;
}

export type ParsedProviderParam =
  | { kind: "uuid"; uuid: string }
  | { kind: "public_id"; publicId: string; slug: string }
  | { kind: "slug"; shortId: string; slug: string }
  | { kind: "invalid" };

export function parseProviderParam(raw: string): ParsedProviderParam {
  const s = (raw || "").trim();
  if (!s) return { kind: "invalid" };
  if (UUID_RE.test(s)) return { kind: "uuid", uuid: s.toLowerCase() };
  // Slug with trailing HL####### segment (preferred).
  const pub = s.match(/-(HL\d{7})$/i);
  if (pub) return { kind: "public_id", publicId: pub[1].toUpperCase(), slug: s };
  // Bare HL####### with no slug text.
  if (PUBLIC_ID_RE.test(s)) return { kind: "public_id", publicId: s.toUpperCase(), slug: s };
  // Legacy slug: extract trailing 8-char hex segment.
  const m = s.match(/-([0-9a-f]{8})$/i);
  if (m) return { kind: "slug", shortId: m[1].toLowerCase(), slug: s };
  // Bare 8-char hex (no slug text).
  if (/^[0-9a-f]{8}$/i.test(s)) return { kind: "slug", shortId: s.toLowerCase(), slug: s };
  return { kind: "invalid" };
}
